import { useNavigate } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { awaitEmitUtmifyEvent } from '../utils/emitUtmifyEvent';

type Props = {
  socket: any;
  venda: { id: string; pixCopiaCola?: string } | null;
  successUrlExterna?: string;
  failedUrlExterna?: string;
  newTabExternos?: boolean;
};

export function usePixRedirects({
  socket,
  venda,
  successUrlExterna,
  failedUrlExterna,
  newTabExternos = false,
}: Props) {
  const navigate = useNavigate();
  const locked = useRef(false);

  function isHttpUrl(url?: string): boolean {
    if (!url) return false;
    try {
      const u = new URL(url);
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
      return false;
    }
  }

  function navigateExternalNoReferrer(href: string, { newTab = false } = {}) {
    const a = document.createElement('a');
    a.href = href;
    a.rel = newTab ? 'noreferrer noopener' : 'noreferrer';
    (a as any).referrerPolicy = 'no-referrer';
    a.target = newTab ? '_blank' : '_self';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  function navigateSmart(
    urlExterna: string | undefined,
    navigate: ReturnType<typeof useNavigate>,
    rotaInterna: string,
    options?: { state?: any; replace?: boolean; newTab?: boolean },
  ) {
    if (isHttpUrl(urlExterna)) {
      navigateExternalNoReferrer(urlExterna!, { newTab: !!options?.newTab });
    } else {
      navigate(rotaInterna, {
        state: options?.state,
        replace: options?.replace,
      });
    }
  }

  useEffect(() => {
    if (!socket || !venda) return;

    const listener = (data: any) => {
      if (locked.current) return;

      const mesmaVenda =
        (data?.vendaId && data?.vendaId === venda.id) ||
        (data?.qrCode && data?.qrCode === venda?.pixCopiaCola);

      if (!mesmaVenda) return;

      locked.current = true;

      if (data?.type === 'success') {
        void (async () => {
          await awaitEmitUtmifyEvent({ event: 'paid', vendaId: venda.id });
          navigateSmart(successUrlExterna, navigate, '/venda/obrigado', {
            state: venda,
            newTab: newTabExternos,
            replace: true,
          });
        })();
      } else if (data?.type === 'failed') {
        void (async () => {
          await awaitEmitUtmifyEvent({ event: 'refused', vendaId: venda.id });
          navigateSmart(
            failedUrlExterna,
            navigate,
            `/venda/compra-recusada/${venda.id}`,
            { newTab: newTabExternos, replace: true },
          );
        })();
      } else {
        locked.current = false;
      }
    };

    socket.on('venda-pix', listener);
    return () => socket.off('venda-pix', listener);
  }, [
    socket,
    venda,
    successUrlExterna,
    failedUrlExterna,
    newTabExternos,
    navigate,
  ]);
}
